import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { t } from 'i18next';

import {
  MessagingChannelLastMessage,
  principalDisplayName,
} from '~hooks/useMessaging/models';
import { Nullable } from '~types/Nullable';

dayjs.extend(utc);
dayjs.extend(timezone);

export const messagingLastActivityLabel = (lastMessageAt: Dayjs) => {
  const lastMessageAtInUserZone = dayjs.tz(lastMessageAt);
  const today = dayjs.tz();

  if (lastMessageAtInUserZone.isSame(today, 'day')) {
    return lastMessageAtInUserZone.format('h:mm A');
  }

  if (lastMessageAtInUserZone.isSame(today.subtract(1, 'day'), 'day')) {
    return t('messaging.yesterday');
  }

  return lastMessageAtInUserZone.format('MMM D');
};

const previewBody = (lastMessage: MessagingChannelLastMessage) => {
  if (lastMessage.isDeleted) return t('messaging.deleted_message');
  if (lastMessage.excerpt) return lastMessage.excerpt;
  if (lastMessage.fileAttachmentCount > 0) {
    return t('messaging.preview.attachments', {
      count: lastMessage.fileAttachmentCount,
    });
  }

  return '';
};

type MessagingChannelPreviewOptions = {
  shouldNameSender?: boolean;
};

export const messagingChannelPreview = (
  lastMessage: Nullable<MessagingChannelLastMessage>,
  viewerUserId: Nullable<string>,
  { shouldNameSender = false }: MessagingChannelPreviewOptions = {},
) => {
  if (!lastMessage) return t('messaging.no_messages');

  const body = previewBody(lastMessage);
  if (!body) return body;

  // React escapes the text itself; i18next escaping would show an apostrophe as `&#39;`.
  const interpolation = { escapeValue: false };
  const isSentByViewer = Boolean(viewerUserId) && lastMessage.senderId === viewerUserId;

  if (isSentByViewer) {
    return t('messaging.preview.own', { interpolation, preview: body });
  }

  const senderName = shouldNameSender ? principalDisplayName(lastMessage.sender) : null;
  if (!senderName) return body;

  return t('messaging.preview.sender', {
    interpolation,
    preview: body,
    sender: senderName,
  });
};
